import { useEffect, useRef } from 'react';

import { SelectablePartner, useCollections } from 'pages/partners/pages/collections/context';
import { GMetrixTableColumnProps } from 'components/GMetrixTable/components';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { GMetrixTable } from 'components/GMetrixTable/GMetrixTable';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { Country, CountryApi } from 'apis/CountryApi';
import { State, StateApi } from 'apis/StateApi';
import { Col, Row } from 'react-bootstrap';
import { useSuperState } from 'hooks';

type CollectionPartnerSelectStepProps = {
    onSelectedPartnersChange: (partners: SelectablePartner[]) => void;
    selectedPartners: SelectablePartner[];
    validationError?: string;
};

export const CollectionPartnerSelectStep = (props: CollectionPartnerSelectStepProps) => {

    const { onSelectedPartnersChange, selectedPartners, validationError = '' } = props;

    const [state, setState] = useSuperState({
        countries: [] as Country[],
        filterEmail: '',
        filterName: '',
        selectedCountryId: 0,
        selectedStateId: 0,
        states: [] as State[]
    });

    const { availablePartners, showAdvancedColumns } = useCollections();
    const { formatMessage } = useIntl();

    // Keep a ref that is always up-to-date with the latest selectedPartners.
    // GMetrixTable stores the initial columnData in its own state and never
    // syncs it when the prop changes, so the cell-renderer callbacks would
    // otherwise close over a stale selectedPartners value.  Reading through
    // the ref ensures every click — even from a stale closure — sees the
    // current selection.
    const selectedPartnersRef = useRef(selectedPartners);
    selectedPartnersRef.current = selectedPartners;

    useEffect(() => {

        const loadCountries = async () => {

            try {

                const countries = await CountryApi.GetAll();
                setState({ countries });

            } catch (e) {

                // countries list remains empty on error

            }

        };

        loadCountries();

    }, []);

    const handleCountryChange = async (countryId: number) => {

        setState({ selectedCountryId: countryId, selectedStateId: 0, states: [] });

        if (countryId > 0) {

            try {

                const states = await StateApi.GetAll(countryId);
                setState({ states });

            } catch (e) {

                // states list remains empty on error

            }

        }

    };

    const handleClearFilters = () => {

        setState({
            filterEmail: '',
            filterName: '',
            selectedCountryId: 0,
            selectedStateId: 0,
            states: []
        });

    };

    const handleAddPartner = (partner: SelectablePartner) => {

        if (!selectedPartnersRef.current.find(p => p.ID === partner.ID)) {

            onSelectedPartnersChange([...selectedPartnersRef.current, partner]);

        }

    };

    const handleRemovePartner = (partnerId: number) => {

        onSelectedPartnersChange(selectedPartnersRef.current.filter(p => p.ID !== partnerId));

    };

    const selectedPartnerIds = new Set(selectedPartners.map(p => p.ID));

    const filteredPartners = availablePartners.filter(p => {

        if (selectedPartnerIds.has(p.ID)) return false;

        if (state.filterName && !p.Name.toUpperCase().includes(state.filterName.toUpperCase())) return false;

        if (state.filterEmail) {

            const emailMatch = p.ContactEmail?.toUpperCase().includes(state.filterEmail.toUpperCase());
            const userListMatch = p.UserList?.toUpperCase().includes(state.filterEmail.toUpperCase());
            if (!emailMatch && !userListMatch) return false;

        }

        if (state.selectedCountryId > 0 && p.Country?.Id !== state.selectedCountryId) return false;

        if (state.selectedStateId > 0 && p.State?.Id !== state.selectedStateId) return false;

        return true;

    });

    type PartnerRow = SelectablePartner & { Id: number };

    const selectedPartnersData: PartnerRow[] = selectedPartners.map(p => ({ ...p, Id: p.ID }));
    const filteredPartnersData: PartnerRow[] = filteredPartners.map(p => ({ ...p, Id: p.ID }));

    // 1% width forces the cell to shrink to its contents width (button)
    const lastCellStyle = { width: '1%' };

    const selectedColumns: GMetrixTableColumnProps<PartnerRow>[] = [
        { field: 'Name', title: formatMessage({ id: 'organization.7cb537a9', defaultMessage: 'Organization', description: 'New' }) },
        { field: 'Sector', hidden: !showAdvancedColumns, title: formatMessage({ id: 'sector.343b1273', defaultMessage: 'Sector', description: 'New' }) },
        {
            cell: ({ dataItem }) => (
                <td>
                    {dataItem.Country?.Name ?? ''}
                </td>
            ),
            field: 'Country',
            hidden: !showAdvancedColumns,
            title: formatMessage({ id: 'country.05543f81', defaultMessage: 'Country', description: 'New' })
        },
        {
            cell: ({ dataItem }) => (
                <td>
                    {dataItem.Active ? <FormattedMessage defaultMessage="Active" id="active.b9616c88" /> : <FormattedMessage defaultMessage="Inactive" id="inactive.5fb6d21a" />}
                </td>
            ),
            field: 'Active',
            title: formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' })
        },
        {
            cell: ({ dataItem }) => (
                <td>
                    <FormattedDate day="numeric" month="short" value={new Date(dataItem.DateCreate)} year="numeric" />
                </td>
            ),
            field: 'DateCreate',
            title: formatMessage({ id: 'created.8d9f8791', defaultMessage: 'Created' })
        },
        {
            cell: ({ dataItem }) => (
                <td style={lastCellStyle}>
                    <GMetrixButton
                        className="btn-sm"
                        onClick={() => handleRemovePartner(dataItem.ID)}
                        variant="danger"
                    >

                        <FormattedMessage defaultMessage="Remove" id="remove.d34ac1e7" />
                    </GMetrixButton>
                </td>
            ),
            title: ''
        }
    ];

    const availableColumns: GMetrixTableColumnProps<PartnerRow>[] = [
        { field: 'Name', title: formatMessage({ id: 'organization.7cb537a9', defaultMessage: 'Organization', description: 'New' }) },
        { field: 'Sector', hidden: !showAdvancedColumns, title: formatMessage({ id: 'sector.343b1273', defaultMessage: 'Sector', description: 'New' }) },
        {
            cell: ({ dataItem }) => (
                <td>
                    {dataItem.State?.Name ?? ''}
                </td>
            ),
            field: 'State',
            title: formatMessage({ id: 'stateprovince.d2dfa3c8', defaultMessage: 'State/Province' })
        },
        {
            cell: ({ dataItem }) => (
                <td>
                    {dataItem.Country?.Name ?? ''}
                </td>
            ),
            field: 'Country',
            title: formatMessage({ id: 'country.05543f81', defaultMessage: 'Country', description: 'New' })
        },
        {
            cell: ({ dataItem }) => (
                <td>
                    {dataItem.Active ? <FormattedMessage defaultMessage="Active" id="active.b9616c88" /> : <FormattedMessage defaultMessage="Inactive" id="inactive.5fb6d21a" />}
                </td>
            ),
            field: 'Active',
            title: formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' })
        },
        {
            cell: ({ dataItem }) => (
                <td style={lastCellStyle}>
                    <GMetrixButton
                        className="btn-sm"
                        onClick={() => handleAddPartner(dataItem)}
                        variant="primary"
                    >
                        {<FormattedMessage defaultMessage="Select" id="select.57e6e9c7" />}
                    </GMetrixButton>
                </td>
            ),
            title: ''
        }
    ];

    return (
        <div>
            <h5 className="text-strong mb-3">
                {<FormattedMessage defaultMessage="Select the Partners to add to the Association" id="select_the_partners_to_add_to_the_associ.847a2421" />}
            </h5>

            {validationError && (
                <div className="alert alert-danger">
                    {validationError}
                </div>
            )}

            {/* Currently selected partners */}
            <div className="mb-4">
                <h6 className="fw-bold">
                    {<FormattedMessage defaultMessage="Currently Selected Partners" id="currently_selected_partners.70ec89f2" />}
                </h6>
                <GMetrixTable
                    columnData={selectedColumns}
                    noDataMessage={formatMessage({ id: 'no_results_were_found.002d7fd2', defaultMessage: 'No results were found', description: 'Gridview' })}
                    rowData={selectedPartnersData}
                    tableName={formatMessage({ id: 'selectedpartners.5adcd99b', defaultMessage: 'SelectedPartners' })}
                />
            </div>

            <hr />

            {/* Add partners section with filters */}
            <div>
                <h6 className="fw-bold">

                    <FormattedMessage defaultMessage="Add Partners" id="add_partners.fad27b1b" />
                </h6>
                <Row className="mb-3">
                    <Col md={3} sm={6}>
                        <GMetrixInput
                            label={formatMessage({ id: 'organization.7cb537a9', defaultMessage: 'Organization', description: 'New' })}
                            onChange={e => setState({ filterName: e.target.value })}
                            value={state.filterName}
                        />
                    </Col>
                    <Col md={3} sm={6}>
                        <GMetrixInput
                            label={formatMessage({ id: 'email_contact.93dfadf4', defaultMessage: 'Email Contact', description: 'New' })}
                            onChange={e => setState({ filterEmail: e.target.value })}
                            value={state.filterEmail}
                        />
                    </Col>
                    <Col md={3} sm={6}>
                        <GMetrixDropdown
                            dataItemKey="Id"
                            dataItems={state.countries}
                            label={formatMessage({ id: 'country.05543f81', defaultMessage: 'Country', description: 'New' })}
                            onChange={(value: Country) => handleCountryChange(value?.Id ?? 0)}
                            placeholder={formatMessage({ id: 'choose_one.e57d2911', defaultMessage: 'Choose one', description: 'formulario' })}
                            textField="Name"
                            value={state.countries.find(c => c.Id === state.selectedCountryId)}
                        />
                    </Col>
                    <Col md={3} sm={6}>
                        <GMetrixDropdown
                            dataItemKey="Id"
                            dataItems={state.states}
                            label={formatMessage({ id: 'stateprovince.d2dfa3c8', defaultMessage: 'State/Province' })}
                            onChange={(value: State) => setState({ selectedStateId: value?.Id ?? 0 })}
                            placeholder={formatMessage({ id: 'choose_one.e57d2911', defaultMessage: 'Choose one', description: 'formulario' })}
                            textField="Name"
                            value={state.states.find(s => s.Id === state.selectedStateId)}
                        />
                    </Col>
                </Row>
                <div className="d-flex mb-3 gap-2">
                    <GMetrixButton
                        onClick={handleClearFilters}
                        variant="outline-primary"
                    >
                        {<FormattedMessage defaultMessage="Clear All Fields" description="botones" id="clear_all_fields.95014c10" />}
                    </GMetrixButton>
                    <GMetrixButton
                        onClick={() => { /* Filters are applied reactively */ }}
                        variant="primary"
                    >
                        {<FormattedMessage defaultMessage="Search" id="search.0ab06b2c" />}
                    </GMetrixButton>
                </div>

                <GMetrixTable
                    columnData={availableColumns}
                    noDataMessage={formatMessage({ id: 'no_results_were_found.002d7fd2', defaultMessage: 'No results were found', description: 'Gridview' })}
                    rowData={filteredPartnersData}
                    tableName={formatMessage({ id: 'availablepartners.7bb13643', defaultMessage: 'AvailablePartners' })}
                />
            </div>
        </div>
    );

};
